import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import {
  IntegrationsService,
  IntegrationConnection,
  SftpImportHistoryItem,
  SftpImportHistorySortBy,
  SftpImportHistorySortOrder,
  SftpIntegrationSetup,
  SftpNotificationRecipient,
  SftpNotificationSettings,
} from './integrations.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { formatSftpDateTime as formatSftpDateTimeValue, SftpDateTimeValue } from './sftp-date-time.utils';

@Component({
  selector: 'app-integration-status',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NzButtonModule,
    NzTableModule,
    NzPaginationModule,
    NzSelectModule,
    NzTagModule,
    NzAlertModule,
    NzDescriptionsModule,
    NzModalModule,
    NzCheckboxModule,
    TranslateModule
  ],
  templateUrl: './integration-status.component.html',
  styleUrls: ['./integration-status.component.less']
})
export class IntegrationStatusComponent implements OnInit {
  connectionId = '';

  connection: IntegrationConnection | null = null;
  sftpIntegration: SftpIntegrationSetup | null = null;
  sftpHistory: SftpImportHistoryItem[] = [];
  sftpHistoryPage = 1;
  sftpHistoryLimit = 20;
  sftpHistoryTotal = 0;
  sftpHistorySortBy: SftpImportHistorySortBy = 'createdAt';
  sftpHistorySortOrder: SftpImportHistorySortOrder = 'desc';
  sftpHistoryLoading = false;
  sftpNotificationSettings: SftpNotificationSettings = this.defaultSftpNotificationSettings();
  sftpNotificationRecipients: SftpNotificationRecipient[] = [];
  sftpNotificationLoading = false;
  sftpNotificationSaving = false;

  lastRun: any = null;
  runs: any[] = [];
  selectedRunId: string | null = null;

  errors: any[] = [];

  loading = false;
  loadingStatus = false;
  loadingRuns = false;
  loadingErrors = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private authService: AuthService,
    private translate: TranslateService,
    private message: NzMessageService,
    private modal: NzModalService
  ) {}

  ngOnInit(): void {
    this.connectionId = this.route.snapshot.paramMap.get('id') || '';
    this.refreshAll();
  }

  refreshAll(): void {
    if (!this.connectionId) return;
    this.loadConnection();
  }

  loadConnection(): void {
    this.integrationsService.getConnection(this.connectionId).subscribe({
      next: (res) => {
        this.connection = res;
        if (res.provider === 'sftp' && res.connectionType === 'sftp_ingestion') {
          this.integrationsService.getSftpIntegration().subscribe({
            next: ({ item }) => {
              this.sftpIntegration = item;
              this.loadSftpHistory();
              this.loadSftpNotificationSettings();
            },
            error: () => (this.sftpIntegration = null),
          });
          return;
        }
        this.loadStatus();
        this.loadRuns();
      },
      error: () => {}
    });
  }

  loadStatus(): void {
    this.loadingStatus = true;
    this.integrationsService.getStatus(this.connectionId).subscribe({
      next: (res) => {
        this.lastRun = res.lastRun || null;
        if (this.lastRun?._id) {
          this.selectedRunId = this.lastRun._id;
          this.loadErrors(this.lastRun._id);
        }
        this.loadingStatus = false;
      },
      error: () => {
        this.loadingStatus = false;
        this.message.error('Failed to load status');
      }
    });
  }

  loadRuns(): void {
    this.loadingRuns = true;
    this.integrationsService.getRuns(this.connectionId).subscribe({
      next: (res) => {
        this.runs = res.items || [];
        this.loadingRuns = false;

        if (!this.selectedRunId && this.runs.length) {
          this.selectedRunId = this.runs[0]._id;
          this.lastRun = this.runs[0];
          if (this.selectedRunId) {
            this.loadErrors(this.selectedRunId);
          }
        }
      },
      error: () => (this.loadingRuns = false)
    });
  }

  onRunChange(runId: string): void {
    const run = (this.runs || []).find(r => r._id === runId);
    if (run) this.lastRun = run;
    this.loadErrors(runId);
  }

  loadErrors(runId: string): void {
    this.loadingErrors = true;
    this.integrationsService.getRunErrors(this.connectionId, runId).subscribe({
      next: (res) => {
        this.errors = res.items || [];
        this.loadingErrors = false;
      },
      error: () => (this.loadingErrors = false)
    });
  }

  runSync(): void {
    this.loading = true;
    this.integrationsService.runSync(this.connectionId).subscribe({
      next: () => {
        this.loading = false;
        this.message.success('Sync started');
        this.refreshAll();
      },
      error: (err) => {
        this.loading = false;
        this.message.error(err?.error?.message || 'Failed to start sync');
      }
    });
  }

  testConnection(): void {
    this.integrationsService.testConnection(this.connectionId).subscribe({
      next: (res) => this.message.success(res.ok ? 'Connection OK' : (res.message || 'Connection failed')),
      error: (err) => this.message.error(err?.error?.message || 'Connection test failed')
    });
  }

  goToSetup(): void {
    if (!this.connection?.provider) return;
    this.router.navigate(['/company/integrations', this.connection.provider, 'setup']);
  }

  lifecycleLabel(status?: string): string {
    return `INTEGRATION.SFTP.LIFECYCLE_LABELS.${status || 'error'}`;
  }

  importStatusLabel(status?: string): string {
    return `INTEGRATION.SFTP.IMPORT_STATUS.${status || 'unknown'}`;
  }

  formatSftpDateTime(value: SftpDateTimeValue): string {
    return formatSftpDateTimeValue(value, this.translate.currentLang || this.translate.defaultLang);
  }

  get isSftp(): boolean {
    return this.connection?.provider === 'sftp' && this.connection?.connectionType === 'sftp_ingestion';
  }

  get canViewSftpImportResult(): boolean {
    return this.authService.hasPermission('company.properties.read');
  }

  get canEditSftpNotifications(): boolean {
    return this.authService.getCurrentCompanyRole() === 'company_admin'
      && this.authService.hasPermission('company.integrations.write');
  }

  private defaultSftpNotificationSettings(): SftpNotificationSettings {
    return {
      recipientUserIds: [],
      events: { success: true, issues: true, failure: true },
    };
  }

  loadSftpHistory(page = this.sftpHistoryPage): void {
    if (!this.isSftp || !this.connectionId) return;
    this.sftpHistoryLoading = true;
    this.integrationsService.getSftpImportHistory(
      this.connectionId,
      page,
      this.sftpHistoryLimit,
      this.sftpHistorySortBy,
      this.sftpHistorySortOrder
    ).subscribe({
      next: (response) => {
        this.sftpHistory = response.items || [];
        this.sftpHistoryPage = response.page;
        this.sftpHistoryLimit = response.limit;
        this.sftpHistoryTotal = response.total;
        this.sftpHistorySortBy = response.sortBy || this.sftpHistorySortBy;
        this.sftpHistorySortOrder = response.sortOrder || this.sftpHistorySortOrder;
        this.sftpHistoryLoading = false;
      },
      error: () => {
        this.sftpHistory = [];
        this.sftpHistoryLoading = false;
      },
    });
  }

  onSftpHistoryPageChange(page: number): void {
    this.loadSftpHistory(page);
  }

  onSftpHistoryPageSizeChange(limit: number): void {
    if (![20, 50, 100].includes(limit)) return;
    this.sftpHistoryLimit = limit;
    this.sftpHistoryPage = 1;
    this.loadSftpHistory(1);
  }

  sftpHistorySortOrderFor(field: SftpImportHistorySortBy): 'ascend' | 'descend' | null {
    if (this.sftpHistorySortBy !== field) return null;
    return this.sftpHistorySortOrder === 'asc' ? 'ascend' : 'descend';
  }

  onSftpHistorySortChange(field: SftpImportHistorySortBy, order: string | null): void {
    const normalizedOrder = order === 'ascend' || order === 'descend' ? order : null;
    this.sftpHistorySortBy = normalizedOrder ? field : 'createdAt';
    this.sftpHistorySortOrder = normalizedOrder === 'ascend' ? 'asc' : 'desc';
    this.sftpHistoryPage = 1;
    this.loadSftpHistory(1);
  }

  loadSftpNotificationSettings(): void {
    if (!this.isSftp || !this.connectionId) return;
    this.sftpNotificationLoading = true;
    this.integrationsService.getSftpNotificationSettings(this.connectionId).subscribe({
      next: ({ settings }) => {
        this.sftpNotificationSettings = settings || this.defaultSftpNotificationSettings();
        this.sftpNotificationLoading = false;
        if (this.canEditSftpNotifications) this.loadSftpNotificationRecipients();
      },
      error: () => (this.sftpNotificationLoading = false),
    });
  }

  private loadSftpNotificationRecipients(): void {
    this.integrationsService.getSftpNotificationRecipients(this.connectionId).subscribe({
      next: ({ items }) => (this.sftpNotificationRecipients = items || []),
      error: () => (this.sftpNotificationRecipients = []),
    });
  }

  toggleSftpNotificationRecipient(userId: string, checked: boolean): void {
    const selected = new Set(this.sftpNotificationSettings.recipientUserIds || []);
    if (checked) selected.add(userId); else selected.delete(userId);
    this.sftpNotificationSettings = {
      ...this.sftpNotificationSettings,
      recipientUserIds: [...selected],
    };
  }

  saveSftpNotificationSettings(): void {
    if (!this.canEditSftpNotifications || !this.connectionId || this.sftpNotificationSaving) return;
    this.sftpNotificationSaving = true;
    this.integrationsService.updateSftpNotificationSettings(this.connectionId, this.sftpNotificationSettings).subscribe({
      next: ({ settings }) => {
        this.sftpNotificationSettings = settings;
        this.sftpNotificationSaving = false;
        this.message.success(this.translate.instant('INTEGRATION.SFTP.NOTIFICATIONS.SAVED'));
      },
      error: (error) => {
        this.sftpNotificationSaving = false;
        this.message.error(error?.error?.message || error?.message || this.translate.instant('INTEGRATION.SFTP.NOTIFICATIONS.ERROR_SAVE'));
      },
    });
  }

  viewSftpImportResult(jobId: string): void {
    if (!this.canViewSftpImportResult || !jobId) return;
    void this.router.navigate(['/properties'], { queryParams: { importJobId: jobId } });
  }

  changeSftpLifecycle(action: 'pause' | 'resume' | 'disconnect'): void {
    if (!this.connectionId) return;
    this.integrationsService.changeSftpLifecycle(this.connectionId, action).subscribe({
      next: ({ item }) => {
        if (action === 'disconnect') {
          this.sftpIntegration = item;
          this.router.navigate(['/company/integrations']);
          return;
        }
        this.refreshAll();
      },
      error: (error) => this.message.error(error?.error?.message || 'Failed to update SFTP integration.'),
    });
  }

  togglePaused(paused: boolean): void {
    if (!this.connectionId) return;
    this.integrationsService.updateConnection(this.connectionId, {
      settings: { ...(this.connection?.settings || {}), paused }
    }).subscribe({
      next: () => {
        this.message.success(paused ? 'Paused' : 'Resumed');
        this.refreshAll();
      },
      error: (err) => this.message.error(err?.error?.message || 'Failed to update pause state')
    });
  }

  disconnect(): void {
    this.modal.confirm({
      nzTitle: 'Disconnect provider?',
      nzContent: `This will remove the connection. You can reconnect later.`,
      nzOkText: 'Disconnect',
      nzOkDanger: true,
      nzOnOk: () => {
        return new Promise<void>((resolve, reject) => {
          this.integrationsService.deleteConnection(this.connectionId).subscribe({
            next: () => {
              this.message.success('Disconnected');
              this.router.navigate(['/company/integrations']);
              resolve();
            },
            error: (err) => {
              this.message.error(err?.error?.message || 'Failed to disconnect');
              reject();
            }
          });
        });
      }
    });
  }

  runTagColor(status: string): string {
    if (status === 'success') return 'green';
    if (status === 'partial') return 'orange';
    if (status === 'failed') return 'red';
    if (status === 'running') return 'blue';
    return 'default';
  }
}
